import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Search, Video, Share2, Heart } from "lucide-react";
import { Button } from "@/components/ui/button";
import RevealImage from "@/components/RevealImage";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};
const stagger = { visible: { transition: { staggerChildren: 0.15 } } };

const process_steps = [
  {
    icon: Search,
    title: "Discover",
    description: "We listen first. We seek out stories that matter, building trust and understanding before the camera ever rolls.",
  },
  {
    icon: Video,
    title: "Film",
    description: "We document with intention, capturing real moments and real voices with honesty and care.",
  },
  {
    icon: Share2,
    title: "Share",
    description: "We release stories through film, media, and community platforms to reach the people who need to see them most.",
  },
  {
    icon: Heart,
    title: "Support",
    description: "We don't stop at awareness. We support creators, communities, and causes connected to each story whenever possible.",
  },
];

const projects = [
  {
    title: "HELENE: Ignoring The Red Tape",
    description: "This film dives deep into the aftermath of one of the most catastrophic storms to hit the region.",
    youtubeId: "_9qSuF98nm8",
    youtubeUrl: "https://www.youtube.com/watch?v=_9qSuF98nm8&t=647s",
    slug: "helene",
  },
  {
    title: "Senior Citizen Trafficking Is Real",
    description: "This documentary uncovers a side of trafficking most people don't even know exists.",
    youtubeId: "ZlidR_eW7Go",
    youtubeUrl: "https://www.youtube.com/watch?v=ZlidR_eW7Go&t=414s",
    slug: "senior-trafficking",
  },
  {
    title: "COSMO CAN'T DIE",
    description: "This documentary follows his fight, his family's strength, and the relentless will to rise.",
    image: "/cosmo-photo.png",
    youtubeId: null,
    youtubeUrl: null,
    slug: "cosmo",
    comingSoon: true,
  },
];

export default function AboutPage() {
  return (
    <div className="font-body" data-testid="about-page">
      {/* Hero */}
      <section className="pt-32 pb-24 lg:pt-40 lg:pb-32" data-testid="about-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div initial="hidden" animate="visible" variants={stagger}>
              <motion.p variants={fadeUp} className="text-xs uppercase tracking-[0.25em] text-[#C86B53] font-semibold mb-4">
                Who We Are
              </motion.p>
              <motion.h1 variants={fadeUp} className="font-heading text-5xl sm:text-6xl font-bold tracking-tight leading-none text-[#292524] mb-8">
                About Visible Foundation
              </motion.h1>
              <motion.p variants={fadeUp} className="text-base font-medium leading-relaxed text-[#57534E] mb-6">
                Visible Foundation is a nonprofit media initiative dedicated to making the unseen
                seen. We exist to bring light to overlooked stories, unheard voices, and hidden
                struggles through film, storytelling, and intentional partnership.
              </motion.p>
              <motion.p variants={fadeUp} className="text-base font-medium leading-relaxed text-[#57534E]">
                By telling the truth with care and conviction, we believe awareness leads to
                understanding &mdash; and understanding creates real transformation.
              </motion.p>
            </motion.div>
            <RevealImage
              src="/about-hero.png"
              alt="Behind the scenes - camera monitor during documentary filming"
              aspectRatio="aspect-[4/5]"
              className="about-hero-image"
            />
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-24 lg:py-32 bg-[#F2EDE4]" data-testid="our-story-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={stagger}
            className="max-w-3xl"
          >
            <motion.p variants={fadeUp} className="text-xs uppercase tracking-[0.2em] text-[#C86B53] font-semibold mb-4">
              Our Story
            </motion.p>
            <motion.h2 variants={fadeUp} className="font-heading text-4xl sm:text-5xl font-bold tracking-tight text-[#292524] mb-8">
              Why We Exist
            </motion.h2>
            <motion.p variants={fadeUp} className="text-base font-medium leading-relaxed text-[#57534E] mb-6">
              Visible Foundation was created because too many important stories go untold. Not
              because they lack meaning, but because they lack visibility. We saw real people, real
              communities, and real crises being ignored, misunderstood, or misrepresented.
            </motion.p>
            <motion.p variants={fadeUp} className="text-base font-medium leading-relaxed text-[#57534E] mb-6">
              We exist to change that. At our core, we believe light reveals truth, and truth has the
              power to restore dignity, inspire action, and spark hope.
            </motion.p>
            <motion.p variants={fadeUp} className="text-base font-medium leading-relaxed text-[#57534E]">
              Through documentary filmmaking and media, we partner with individuals and organizations
              to tell stories the right way &mdash; with integrity, respect, and purpose.
            </motion.p>
            <motion.div variants={fadeUp} className="mt-8">
              <Link to="/contact-us">
                <Button className="bg-[#C86B53] hover:bg-[#b55c46] text-white rounded-sm px-6 h-10 text-sm font-semibold tracking-wide" data-testid="about-contact-button">
                  Contact Us
                </Button>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* How We Work */}
      <section className="py-24 lg:py-32" data-testid="how-we-work-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={stagger}
          >
            <motion.p variants={fadeUp} className="text-xs uppercase tracking-[0.2em] text-[#C86B53] font-semibold mb-4">
              Our Process
            </motion.p>
            <motion.h2 variants={fadeUp} className="font-heading text-4xl sm:text-5xl font-bold tracking-tight text-[#292524] mb-4">
              How We Work
            </motion.h2>
            <motion.p variants={fadeUp} className="text-base font-medium text-[#57534E] max-w-2xl mb-16">
              We crowdfund each project independently, giving donors a clear connection to the
              stories they support. After production, remaining funds are directed toward the causes
              and nonprofit organizations tied to each film.
            </motion.p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {process_steps.map((step, i) => (
              <motion.div
                key={step.title}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.5, delay: i * 0.12 }}
                className="bg-white border border-[rgba(41,37,36,0.08)] rounded-sm p-8 flex flex-col items-center text-center hover:shadow-lg hover:border-[#C86B53]/20 transition-all duration-300"
                data-testid={`process-step-${step.title.toLowerCase()}`}
              >
                <div className="w-16 h-16 rounded-full bg-[#C86B53]/10 flex items-center justify-center mb-6">
                  <step.icon size={28} strokeWidth={1.8} className="text-[#C86B53]" />
                </div>
                <h3 className="font-heading text-2xl font-bold text-[#292524] mb-3">
                  {step.title}
                </h3>
                <p className="text-sm font-medium text-[#57534E] leading-relaxed">
                  {step.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Recent Projects */}
      <section className="py-24 lg:py-32 bg-[#F2EDE4]" data-testid="about-projects-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={stagger}
          >
            <motion.p variants={fadeUp} className="text-xs uppercase tracking-[0.2em] text-[#C86B53] font-semibold mb-4">
              Our Work
            </motion.p>
            <motion.h2 variants={fadeUp} className="font-heading text-4xl sm:text-5xl font-bold tracking-tight text-[#292524] mb-16">
              Our Recent Projects
            </motion.h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {projects.map((project, i) => (
              <motion.div
                key={project.slug}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.6, delay: i * 0.15 }}
              >
                {project.youtubeId ? (
                  <div className="video-container rounded-sm overflow-hidden shadow-md">
                    <iframe
                      src={`https://www.youtube.com/embed/${project.youtubeId}`}
                      title={project.title}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      referrerPolicy="strict-origin-when-cross-origin"
                      allowFullScreen
                    />
                  </div>
                ) : project.image ? (
                  <div className="relative aspect-video rounded-sm overflow-hidden bg-black">
                    <img src={project.image} alt={project.title} className="w-full h-full object-contain" />
                    <div className="absolute top-3 right-3">
                      <span className="bg-[#C86B53] text-white text-[10px] uppercase tracking-[0.2em] px-4 py-1.5 rounded-sm font-semibold">
                        Coming Soon
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="relative aspect-video rounded-sm overflow-hidden bg-[#292524] flex items-center justify-center">
                    <div className="text-center px-6">
                      <p className="font-heading text-2xl font-bold text-white mb-2">{project.title}</p>
                      <span className="bg-[#C86B53] text-white text-[10px] uppercase tracking-[0.2em] px-4 py-1.5 rounded-sm font-semibold">
                        Coming Soon
                      </span>
                    </div>
                  </div>
                )}
                <h3 className="font-heading text-xl font-bold text-[#292524] mt-5 mb-2">
                  {project.title}
                </h3>
                <p className="text-sm font-medium text-[#57534E] leading-relaxed mb-4">
                  {project.description}
                </p>
                {project.youtubeUrl ? (
                  <Link
                    to="/films"
                    className="text-sm font-semibold text-[#C86B53] hover:text-[#b55c46] inline-flex items-center gap-1 transition-colors"
                    data-testid={`about-project-watch-${project.slug}`}
                  >
                    Watch Now
                    <ArrowRight size={14} strokeWidth={2} />
                  </Link>
                ) : (
                  <span className="text-sm font-semibold text-[#57534E]">Coming Soon</span>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
